/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

// TODO Unit testy.
// - Napřed vyčlenit testy `StateMachine` z `ViewerLifeCycle`.
// TODO Problém s navazavání událostí na seznam slajdů.
// - Prolínač má několik slajdů a je nutné identifikovat, který událost vyvolal.
// - Zároveň je ale nutné mít listeren uložený, aby šel přidat při #off.
// - Možná řešení:
//   - Aktuálně hotfixově přidává argument samotný viewerSlide: .emit('played', this).
//   - Zneužít #on argument context {viewerList, viewerSlide} místo viewerList.
//   - Úplně nejlépe by mělo být umožněno při #on vkládat parametry, které by se pak předali.
//   - Případně mít `onPlayingError` a přepínat naslouchání dle změn ve přiřazení slajdu.
//   - Nejhorší varianta asi ukládat listener pro každý slajd v obalovacím objektu objetku: {viewerSlide, onError, onPlayed, ...}
/**
 * Slajd přehrávače obsahu.
 * Reprezentuje přehrání naplánovaného média v daný okamžik na daném zdroji.
 * Narozdíl od média se slajd při přehrání nikdy neopakuje.
 * Pro každé opakované přehrání média se vždy vytváří nový slajd.
 *
 * ![Diagram stavového automatu](./core/viewer/slide/viewer-slide.doc.png)
 * @implements {Destroyable}
 * @implements {EventBus}
 */
class ViewerSlide {
  /**
   * Vytvoří instanci.
   * @param {HTMLElement} parentEl Rodičovský element, do kterého se vloží elementy slajdu.
   * @param {ScheduleInfo} scheduleInfo Informace o přehrání média, které má slajd přehrát.
   * @param {StateMachineFactory} stateMachineFactory
   * @returns {ViewerSlide} Nová instance.
   */
  constructor(parentEl, scheduleInfo, stateMachineFactory) {
    if (!isObject(parentEl)) {
      throw new TypeError(`Invalid parent element ${parentEl}.`);
    }
    if (!isObject(scheduleInfo)) {
      throw new TypeError(`Invalid schedule info ${scheduleInfo}.`);
    }
    /**
     * Ovladač, který řídí zobrazení slajdu.
     * @private
     * @type {?ViewerController}
     */
    this.controller = null;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.failed = false;
    this.parentEl = parentEl;
    this.played = false;
    this.scheduleInfo = scheduleInfo;
    /**
     * Zdroj, který poskytuje prostředky pro zobrazení slajdu.
     * @private
     * @type {?ViewerSource}
     */
    this.source = null;
    /**
     * Stavový automat životního cyklu.
     * @private
     * @type {StateMachine}
     */
    this.stateMachine = stateMachineFactory.create({
      init: 'created',
      transitions: [
        {
          name: 'delete',
          from: ['created', 'entered', 'left', 'seized'],
          to: 'deleted'
        },
        { name: 'enter', from: 'seized', to: 'entered' },
        { name: 'leave', from: 'entered', to: 'left' },
        { name: 'release', from: ['left', 'seized'], to: 'released' },
        { name: 'seize', from: 'created', to: 'seized' }
      ]
    });
    // Navázání naslouchání na instanci.
    this.onError = this.onError.bind(this);
    this.onLoaded = this.onLoaded.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
  }

  /**
   * Vytvoří ovladač a naváže naslouchání jeho událostem.
   * @param {ViewerType} viewerType Typ média, který má ovladač vytvořit.
   * @private
   * @returns {ViewerController} Vytvořený ovladač.
   */
  createController(viewerType) {
    const controller = viewerType.createController();
    controller.on('error', this.onError);
    controller.on('loaded', this.onLoaded);
    controller.on('played', this.onPlayed);
    return controller;
  }

  /**
   * Smaže poškozený slajd.
   * @returns {Promise.<undefined>} Vyřeší se po smazání.
   */
  delete() {
    return this.stateMachine.transitAsync('delete', () => {
      const { controller } = this;
      if (!isNil(controller)) {
        this.destroyController(controller);
        this.controller = null;
      }
      const { source } = this;
      if (isNil(source)) {
        return;
      }
      return this.deleteSource(source).then(() => {
        this.source = null;
      });
    });
  }

  /**
   * Smaže poškozený zdroj zobrazování slajdu.
   * @param {ViewerSource} source Zdroj, který se má smazat.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po smazání.
   */
  deleteSource(source) {
    // Zrušení zdroje.
    const viewerType = this.scheduleInfo.getViewerType();
    const sourceCreator = viewerType.getSourceCreator();
    return sourceCreator.delete(source);
  }

  /** @inheritdoc */
  destroy() {
    // TODO #458 Technické dluhy spojené s rušením vlákna prolínače.
    // - Není oficiálně podporovaný a otestovaný destroy v jiném stavu, než po volání #release.
    // - Není dodrženo minimálně ve ViewerThread#destroy, který se volá z ViewerDirector#destroy
    const { controller } = this;
    if (!isNil(controller)) {
      this.destroyController(controller);
    }
  }

  /**
   * Zruší ovladač a uvolní naslouchání jeho událostem.
   * @param {ViewerController} controller Ovladač, který se má zrušit.
   * @private
   * @returns {undefined} Nic.
   */
  destroyController(controller) {
    controller.off('error', this.onError);
    controller.off('loaded', this.onLoaded);
    controller.off('played', this.onPlayed);
    controller.destroy();
  }

  /**
   * Zahájí zobrazení a přehrávání slajdu.
   * @returns {Promise.<undefined>} Vyřeší se po zobrazení.
   */
  enter() {
    return this.stateMachine.transitAsync('enter', () =>
      this.controller.enter()
    );
  }

  /**
   * Zjistí informace o přehrání média, které slajd zobrazuje.
   * @returns {ScheduleInfo} Informace o přehrání média.
   */
  getScheduleInfo() {
    return this.scheduleInfo;
  }

  /**
   * Zjistí, jestli byl slajd označen jako poškozený.
   * @returns {boolean} Příznak, že je slajd poškozený.
   */
  isFailed() {
    return this.failed;
  }

  /**
   * Zjistí, jestli byl slajd označen jako přehraný.
   * @returns {boolean} Příznak, že je slajd přehraný.
   */
  isPlayed() {
    return this.played;
  }

  /**
   * Ukončí zobrazení a přehrávání slajdu.
   * @returns {Promise.<undefined>} Vyřeší se po skrytí slajdu.
   */
  leave() {
    return this.stateMachine.transitAsync('leave', () =>
      this.controller.leave()
    );
  }

  /**
   * Uvolní zdroj zobrazování slajdu.
   * @returns {Promise.<undefined>} Vyřeší se po uvolnění.
   */
  release() {
    // TODO Vyčlenit metodu #return pro operace s ViewerSource?
    return this.stateMachine.transitAsync('release', () => {
      const { controller } = this;
      return controller.release().then(() => {
        this.destroyController(controller);
        this.controller = null;
        const viewerType = this.scheduleInfo.getViewerType();
        const sourceCreator = viewerType.getSourceCreator();
        return sourceCreator.receive(this.source).then(() => {
          this.source = null;
        });
      });
    });
  }

  /**
   * Zabere zdroj a začne načítat slajd.
   * @returns {Promise.<undefined>} Vyřeší se po zabrání.
   */
  seize() {
    // TODO Vyčlenit metodu #borrow pro operace s ViewerSource?
    return this.stateMachine.transitAsync('seize', () => {
      const { scheduleInfo } = this;
      const viewerType = scheduleInfo.getViewerType();
      const sourceCreator = viewerType.getSourceCreator();
      return sourceCreator.lend(this.parentEl, scheduleInfo).then((source) => {
        this.source = source;
        const controller = this.createController(viewerType);
        this.controller = controller;
        return controller.seize(scheduleInfo, source);
      });
    });
  }

  /**
   * Označí slajd jako poškozený.
   * @param {boolean} value Příznak, jestli je slajd poškozený.
   * @returns {undefined} Nic.
   */
  setFailed(value) {
    if (!isBoolean(value)) {
      throw new TypeError(`Invalid value ${value}.`);
    }
    this.failed = value;
  }

  /**
   * Označí slajd jako přehraný.
   * @param {boolean} value Příznak, jestli je slajd přehraný.
   * @returns {undefined} Nic.
   */
  setPlayed(value) {
    if (!isBoolean(value)) {
      throw new TypeError(`Invalid value ${value}.`);
    }
    this.played = value;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.emitter.emit('error', this, error);
  }

  /**
   * Reakce na načtení slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onLoaded() {
    this.emitter.emit('loaded', this);
  }

  /**
   * Reakce na přehrání slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed() {
    this.emitter.emit('played', this);
  }
}

/**
 * Chybová událost.
 * @event ViewerSlide#error
 * @param {ViewerSlide} viewerSlide Slajd, ve kterém událost vznikla.
 * @param {Error} error Chybové hlášení.
 */

/**
 * Dokončení načítání slajdu.
 * Musí být vyvoláno jednou po převzetí elementu.
 * @event ViewerSlide#loaded
 * @param {ViewerSlide} viewerSlide Slajd, ve kterém událost vznikla.
 */

/**
 * Dosažení konce přehrávání slajdu.
 * Některé slajdy nemusí vyvolat, pokud médium není určeno k přehrání.
 * @event ViewerSlide#played
 * @param {ViewerSlide} viewerSlide Slajd, ve kterém událost vznikla.
 */

export default ViewerSlide;
